/**
 * Canonical set of marketing channels we attribute store traffic to via `utm_source`.
 * Shared by the share dialog, the public visit-tracking endpoint and the traffic report.
 * Unknown sources are discarded — we only persist attributed traffic from these channels.
 *
 * `meta` sits alongside `facebook`/`instagram` rather than replacing them (VT-1046): a link the
 * merchant posts himself belongs to the network he posted it on, while a Meta Ads click covers
 * both placements at once and cannot be split back into one of them.
 */

export const UTM_SOURCES = [
  'whatsapp',
  'google',
  'facebook',
  'instagram',
  'meta',
  'tiktok',
] as const;

export type UtmSource = (typeof UTM_SOURCES)[number];

const UTM_SOURCE_SET = new Set<string>(UTM_SOURCES);

/** Short forms merchants hand-type when tagging their own links, folded into the real channel. */
const UTM_SOURCE_ALIASES: Readonly<Record<string, UtmSource>> = {
  fb: 'facebook',
  ig: 'instagram',
  'tik-tok': 'tiktok',
  tik_tok: 'tiktok',
};

/** Lowercase + trim a raw `utm_source`, fold known aliases and validate against the channels. */
export function normalizeUtmSource(raw: unknown): UtmSource | null {
  if (typeof raw !== 'string') return null;
  const value = raw.trim().toLowerCase();
  if (UTM_SOURCE_SET.has(value)) return value as UtmSource;
  return UTM_SOURCE_ALIASES[value] ?? null;
}

/** Max stored length of a campaign tag — bounds report cardinality and storage. */
export const UTM_CAMPAIGN_MAX_LENGTH = 60;

/**
 * Normalize a raw `utm_campaign`. Unlike `utm_source`, campaigns are open-ended (the marketing
 * team names each sponsored link), so we sanitize rather than validate against a fixed set:
 * lowercase, trim, keep only `[a-z0-9._-]`, cap length. Returns null when nothing usable remains.
 */
export function normalizeUtmCampaign(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const value = raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]/g, '')
    .slice(0, UTM_CAMPAIGN_MAX_LENGTH);
  return value.length > 0 ? value : null;
}
